/* eslint-disable react/no-unknown-property */
import {
  faCamera,
  faCaretDown,
  faChildReaching,
  faXRay,
} from "@fortawesome/pro-solid-svg-icons";
import { Box, Menu, Stack } from "@mui/material";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import {
  OrbitControls,
  OrbitControlsChangeEvent,
  TransformControls,
  useGLTF,
} from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import { cloneDeep as cloneObject } from "lodash";
import {
  Dispatch,
  forwardRef,
  SetStateAction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { CatmullRomCurve3, Object3D, PerspectiveCamera, Vector3 } from "three";
import { clone } from "three/examples/jsm/utils/SkeletonUtils";
import { OrbitControls as OrbitControlsImpl } from "three-stdlib";

import { Icon } from "@/components/ui-v2/Icon/Icon";
import { MenuItem } from "@/components/ui-v2/Menu/components/MenuItem";
import {
  bindMenu,
  bindMenuTrigger,
  useMenuState,
} from "@/components/ui-v2/Menu/hooks/useMenuState";
import { roundTo } from "@/utils/numbers";

import type {
  BoneName as JointName,
  BoneWithName,
  PoseData,
  PoseViewData,
  ViewPresetData,
} from "../../../../types";
import {
  cameraFov,
  exportGLTF,
  urlWithCacheVersion,
  viewPresets,
} from "../../../../utils";

const controlledJointNames: JointName[] = [
  "mixamorigSpine",
  "mixamorigHead",
  "mixamorigRightArm",
  "mixamorigRightForeArm",
  "mixamorigLeftArm",
  "mixamorigLeftForeArm",
  "mixamorigLeftLeg",
  "mixamorigRightLeg",
  "mixamorigLeftUpLeg",
  "mixamorigRightUpLeg",
  "mixamorigHips",
];

// color reference chart at https://github.com/lllyasviel/ControlNet/discussions/266
const openPoseJoints: [JointName, number][] = [
  // name, color
  ["mixamorigNose", 0xff0000],
  ["mixamorigNeck", 0xff5500],
  ["mixamorigRightArm", 0xffaa00],
  ["mixamorigRightForeArm", 0xffff00],
  ["mixamorigRightHand", 0xaaff00],
  ["mixamorigLeftArm", 0x55ff00],
  ["mixamorigLeftForeArm", 0x00ff00],
  ["mixamorigLeftHand", 0x00ff55],
  ["mixamorigRightUpLeg", 0x00ffaa],
  ["mixamorigRightLeg", 0x00ffff],
  ["mixamorigRightFoot", 0x00aaff],
  ["mixamorigLeftUpLeg", 0x0055ff],
  ["mixamorigLeftLeg", 0x0000ff],
  ["mixamorigLeftFoot", 0x5500ff],
  ["mixamorigRightEye", 0xaa00ff],
  ["mixamorigLeftEye", 0xff00ff],
  ["mixamorigRightEar", 0xff00aa],
  ["mixamorigLeftEar", 0xff0055],
];
const openPoseBones: [JointName, JointName, number][] = [
  // start joint, end joint, color
  ["mixamorigNeck", "mixamorigRightArm", 0x990000],
  ["mixamorigNeck", "mixamorigLeftArm", 0x993300],
  ["mixamorigRightArm", "mixamorigRightForeArm", 0x996600],
  ["mixamorigRightForeArm", "mixamorigRightHand", 0x999900],
  ["mixamorigLeftArm", "mixamorigLeftForeArm", 0x669900],
  ["mixamorigLeftForeArm", "mixamorigLeftHand", 0x339900],
  ["mixamorigNeck", "mixamorigRightUpLeg", 0x009900],
  ["mixamorigRightUpLeg", "mixamorigRightLeg", 0x009933],
  ["mixamorigRightLeg", "mixamorigRightFoot", 0x009966],
  ["mixamorigNeck", "mixamorigLeftUpLeg", 0x009999],
  ["mixamorigLeftUpLeg", "mixamorigLeftLeg", 0x006699],
  ["mixamorigLeftLeg", "mixamorigLeftFoot", 0x003399],
  ["mixamorigNeck", "mixamorigNose", 0x000099],
  ["mixamorigNose", "mixamorigRightEye", 0x330099],
  ["mixamorigRightEye", "mixamorigRightEar", 0x660099],
  ["mixamorigNose", "mixamorigLeftEye", 0x990099],
  ["mixamorigLeftEye", "mixamorigLeftEar", 0x990066],
];

const ViewButtonsContainer = styled(Stack)(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(2),
  top: theme.spacing(2),
}));

interface CharacterProps {
  poseData: PoseData;
  setPoseData: Dispatch<SetStateAction<PoseData>>;
  onViewChange: (viewData: PoseViewData) => void;
  onPoseChange: () => void;
}

export type CharacterHandle = {
  exportPoseModel: () => Promise<ArrayBuffer>;
  exportPoseImage: () => Promise<string>;
};

export const Mannequin = forwardRef<CharacterHandle, CharacterProps>(function (
  { poseData, onViewChange, onPoseChange, setPoseData },
  ref,
) {
  const modelUrl = `${poseData.modelUrl}?time=${poseData.modelLoadedAt}`;
  const { scene } = useGLTF(urlWithCacheVersion(modelUrl, poseData));
  const orbitControls = useRef<OrbitControlsImpl>(null);
  const skeletonCanvas = useRef<HTMLCanvasElement | null>(null);
  const charObject = useRef<Object3D>(null);
  const [showOrbitTarget, setShowOrbitTarget] = useState(false);
  const [jointControlAnchor, setJointControlAnchor] =
    useState<BoneWithName | null>(null);
  const [, setRenderVersion] = useState(0);
  const cameraAnglesPresets = useMenuState();

  useEffect(() => {
    setShowOrbitTarget(true);
    const showOrbitTargetTimeout = setTimeout(() => {
      setShowOrbitTarget(false);
    }, 3000);
    return () => clearTimeout(showOrbitTargetTimeout);
  }, [poseData.orbitControl.target]);

  const camera = useMemo(() => new PerspectiveCamera(cameraFov), []);
  useEffect(() => {
    camera.position.set(...poseData.camera.position);
    if (orbitControls.current) {
      orbitControls.current.target.set(...poseData.orbitControl.target);
    }
  }, [
    poseData.camera.position,
    poseData.orbitControl.target,
    orbitControls.current,
  ]);

  const handleToggleVariant = () => {
    setPoseData((prev) => ({
      ...prev,
      characterVariant:
        prev.characterVariant === "mannequin" ? "skeleton" : "mannequin",
    }));
  };

  const handleCameraAngleSelect = (preset: ViewPresetData) => () => {
    setPoseData((prev) => ({
      ...prev,
      ...cloneObject(preset.data),
    }));
    cameraAnglesPresets.onClose();
  };

  const renderedScene = useMemo(() => {
    const cloned = clone(scene);
    cloned.traverse((child) => {
      if (child.type === "SkinnedMesh") {
        child.frustumCulled = false;
      }
    });
    setJointControlAnchor(null);
    return cloned;
  }, [scene, poseData.modelLoadVersion]);

  const jointControlBones: BoneWithName[] = useMemo(() => {
    const newBones: BoneWithName[] = [];
    renderedScene.traverse((child) => {
      if (
        "name" in child &&
        controlledJointNames.includes((child as BoneWithName).name)
      ) {
        newBones.push(child as BoneWithName);
      }
    });
    return newBones;
  }, [renderedScene]);

  const onOrbitControlChange = useCallback(
    (e?: OrbitControlsChangeEvent) => {
      if (!e) return;
      // OrbitControlsChangeEvent.target is typed as EventTarget, but at runtime
      // it is an OrbitControlsImpl instance with a Vector3 `target` property.
      const control = e.target as unknown as OrbitControlsImpl;
      const camera = control.object as PerspectiveCamera;
      const updatedPoseViewData: PoseViewData = {
        camera: {
          position: [
            roundTo(camera.position.x, 0.0001),
            roundTo(camera.position.y, 0.0001),
            roundTo(camera.position.z, 0.0001),
          ],
        },
        orbitControl: {
          target: [
            roundTo(control.target.x, 0.0001),
            roundTo(control.target.y, 0.0001),
            roundTo(control.target.z, 0.0001),
          ],
        },
      };
      onViewChange(updatedPoseViewData);
    },
    [onViewChange],
  );

  const onBoneChange = useCallback(() => {
    onPoseChange();
    setRenderVersion((v) => v + 1); // this triggers a rerender to update bone positions for the controllers
  }, [onPoseChange]);

  const getSkeletonBonePosition = useCallback(
    (name: JointName): Vector3 | undefined => {
      if (!charObject.current) return undefined;
      let pos = undefined;
      charObject.current.traverse((child) => {
        if (child.name === name) {
          pos = new Vector3().setFromMatrixPosition(child.matrixWorld);
          return;
        } else if (
          name === "mixamorigRightEar" &&
          child.name === "mixamorigHead"
        ) {
          pos = new Vector3();
          pos.copy(child.position);
          pos.x -= 10;
          pos.y -= 5;
          child.localToWorld(pos);
        } else if (
          name === "mixamorigLeftEar" &&
          child.name === "mixamorigHead"
        ) {
          pos = new Vector3();
          pos.copy(child.position);
          pos.x += 10;
          pos.y -= 5;
          child.localToWorld(pos);
        } else if (name === "mixamorigNose" && child.name === "mixamorigHead") {
          pos = new Vector3();
          pos.copy(child.position);
          pos.y -= 8;
          pos.z += 12;
          child.localToWorld(pos);
        }
      });
      return pos;
    },
    [charObject.current],
  );

  useImperativeHandle(
    ref,
    () => ({
      exportPoseModel: () => {
        return exportGLTF(renderedScene);
      },
      exportPoseImage: () => {
        return Promise.resolve(
          skeletonCanvas.current?.toDataURL("image/png") ?? "",
        );
      },
    }),
    [renderedScene],
  );

  return (
    <>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "auto",
          maxHeight: "100%",
          aspectRatio: "1 / 1",
        }}
      >
        {/* skeleton canvas */}
        <Canvas
          onCreated={() => {
            onBoneChange();
          }}
          gl={{
            preserveDrawingBuffer: true,
            alpha: false,
          }}
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
          }}
          ref={skeletonCanvas}
          camera={camera}
        >
          <ambientLight color={0xffffff} />
          {/* skeleton needs the char to load first */}
          {charObject.current && (
            <>
              {openPoseJoints.map(([name, color], i) => {
                const start = getSkeletonBonePosition(name);

                // return tubegeometry
                return (
                  <mesh key={`joint-${i}`} position={start}>
                    <sphereGeometry args={[0.01, 10, 10]} />
                    <meshStandardMaterial color={color} />
                  </mesh>
                );
              })}
              {openPoseBones.map(([startName, endName, color], i) => {
                const start = getSkeletonBonePosition(startName);
                const end = getSkeletonBonePosition(endName);
                if (!start || !end) return null;
                // Define the radius function
                return (
                  <group key={`bone-${i}`}>
                    <mesh>
                      <tubeGeometry
                        args={[
                          new CatmullRomCurve3([start, end]),
                          1, // path segments
                          0.007, // THICKNESS
                          8, //Roundness of Tube
                          false, //closed
                        ]}
                      />
                      <meshStandardMaterial
                        // depthWrite={false}
                        // depthTest={false}
                        color={color}
                      />
                    </mesh>
                  </group>
                );
              })}
            </>
          )}
        </Canvas>
        {/* mannequin canvas */}
        <Canvas
          gl={{
            preserveDrawingBuffer: true,
            alpha: false,
          }}
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            visibility:
              poseData.characterVariant === "mannequin" ? "visible" : "hidden",
          }}
          camera={camera}
        >
          <group>
            <ambientLight color={0xffffff} />
            <pointLight position={[10, 10, 10]} />
            <primitive ref={charObject} object={renderedScene} />
          </group>
        </Canvas>
        {/* joint control canvas */}
        <Canvas
          gl={{
            preserveDrawingBuffer: true,
            alpha: true,
          }}
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
          }}
          camera={camera}
        >
          <group visible={poseData.characterVariant === "mannequin"}>
            <ambientLight color={0xffffff} />
            <axesHelper args={[0.5]} />
            {/* controllers for each joint in the mannequin view */}
            {jointControlBones.map((bone) => {
              return (
                <mesh
                  key={bone.name}
                  position={new Vector3().setFromMatrixPosition(
                    bone.matrixWorld,
                  )}
                  onClick={(event) => {
                    setJointControlAnchor(bone);
                    event.stopPropagation();
                  }}
                  onPointerMissed={() => {
                    setJointControlAnchor(null);
                  }}
                >
                  <sphereGeometry args={[0.03, 10, 10]} />
                  <meshStandardMaterial
                    depthWrite={false}
                    depthTest={false}
                    color={
                      bone.name === jointControlAnchor?.name
                        ? "#c026d3"
                        : "orange"
                    }
                  />
                </mesh>
              );
            })}

            {/* transform control for joints */}
            {jointControlAnchor && (
              <TransformControls
                space="local"
                object={jointControlAnchor}
                mode="rotate"
                rotationSnap={0}
                onObjectChange={onBoneChange}
              />
            )}
          </group>
          <group visible={showOrbitTarget}>
            <mesh position={poseData.orbitControl.target}>
              <sphereGeometry args={[0.01, 10, 10]} />
              <meshStandardMaterial
                depthWrite={false}
                depthTest={false}
                color="white"
              />
            </mesh>
          </group>
          <OrbitControls
            key="orbit"
            ref={orbitControls}
            makeDefault
            camera={camera}
            target={poseData.orbitControl.target}
            onChange={onOrbitControlChange}
            rotateSpeed={0.6}
            panSpeed={0.6}
            // enablePan={false}
            enableZoom={true}
            minDistance={1}
            maxDistance={2.5}
            zoomSpeed={1}
          />
        </Canvas>
      </Box>
      <ViewButtonsContainer spacing={2} direction="row">
        <Button
          variant="contained"
          color="secondary"
          sx={{ width: "100%" }}
          startIcon={<Icon icon={faCamera} />}
          endIcon={<Icon icon={faCaretDown} />}
          {...bindMenuTrigger(cameraAnglesPresets)}
        >
          Camera Angles
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleToggleVariant}
          startIcon={
            <Icon
              icon={
                poseData.characterVariant === "mannequin"
                  ? faXRay
                  : faChildReaching
              }
            />
          }
        >
          {poseData.characterVariant === "mannequin"
            ? "Switch to Skeleton"
            : "Switch to Mannequin"}
        </Button>
      </ViewButtonsContainer>
      <Menu
        {...bindMenu(cameraAnglesPresets)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            style: {
              minWidth: "150px",
            },
          },
        }}
      >
        {Object.entries(viewPresets).map(([name, preset]) => (
          <MenuItem key={name} onClick={handleCameraAngleSelect(preset)}>
            {preset.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
});
